/** HealthcheckPanel — Visibilidad y control del pipeline inbound de tickets.
 *
 * Muestra:
 *   - Estado del último healthcheck (OK / FAILED / PENDIENTE) + última latencia
 *   - Botón "Probar ahora" → POST /api/tickets/admin/healthcheck/verificar-ahora
 *   - Botón "Refrescar" para recargar logs
 *   - Mini sparkline SVG de las últimas latencias OK
 *   - Tabla con últimos 20 healthchecks
 */
import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import {
  ShieldCheck, RefreshCw, Loader2, AlertTriangle, CheckCircle2, Clock, Activity,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAbortableLoad, isCanceled } from '../../../hooks/useAbortableLoad';

const fmtDate = (s) => s ? new Date(s).toLocaleString('es-PE', { dateStyle: 'short', timeStyle: 'medium' }) : '—';

const STATUS_STYLES = {
  OK:        { label: 'OK',        cls: 'bg-emerald-100 text-emerald-700 border-emerald-200', icon: CheckCircle2 },
  FAILED:    { label: 'FAILED',    cls: 'bg-rose-100 text-rose-700 border-rose-200',          icon: AlertTriangle },
  PENDIENTE: { label: 'PENDIENTE', cls: 'bg-amber-100 text-amber-700 border-amber-200',       icon: Clock },
};

function Sparkline({ values, width = 240, height = 40 }) {
  if (!values || values.length === 0) {
    return <div className="text-xs text-slate-400 italic">Sin datos de latencia aún.</div>;
  }
  const max = Math.max(...values);
  const min = Math.min(...values);
  const rango = Math.max(1, max - min);
  const stepX = values.length > 1 ? width / (values.length - 1) : 0;
  const points = values
    .map((v, i) => {
      const x = i * stepX;
      const y = height - ((v - min) / rango) * (height - 4) - 2;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');
  return (
    <svg width={width} height={height} className="overflow-visible" data-testid="hc-sparkline">
      <polyline
        fill="none"
        stroke="rgb(16 185 129)"
        strokeWidth="1.5"
        points={points}
      />
      {values.map((v, i) => {
        const x = i * stepX;
        const y = height - ((v - min) / rango) * (height - 4) - 2;
        return <circle key={i} cx={x} cy={y} r="2" fill="rgb(16 185 129)" />;
      })}
    </svg>
  );
}

export default function HealthcheckPanel({ api, isAdmin }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [verificando, setVerificando] = useState(false);

  const fetchLogs = async (signal) => {
    setLoading(true);
    try {
      const { data } = await api.get('/tickets/admin/healthcheck/logs', { params: { limit: 20 }, signal });
      setLogs(data || []);
    } catch (e) {
      if (isCanceled(e)) return;
      toast.error(e?.response?.data?.detail || 'Error cargando logs');
    } finally {
      setLoading(false);
    }
  };

  useAbortableLoad((signal) => fetchLogs(signal), []);

  const verificar = async () => {
    setVerificando(true);
    try {
      const { data } = await api.post('/tickets/admin/healthcheck/verificar-ahora');
      toast.success(`Healthcheck enviado (marker: ${data.marker_token?.slice(0, 12)}…)`);
      // Esperar 2s para dar tiempo al inbound y refrescar
      setTimeout(fetchLogs, 2000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error enviando healthcheck');
    } finally {
      setVerificando(false);
    }
  };

  const ultimo = logs[0];
  const latenciasOK = useMemo(
    () => logs.filter((l) => l.status === 'OK' && l.latencia_segs != null).slice(0, 10).reverse().map((l) => l.latencia_segs),
    [logs],
  );
  const stats = useMemo(() => {
    const okCount = logs.filter((l) => l.status === 'OK').length;
    const failed = logs.filter((l) => l.status === 'FAILED').length;
    const pendiente = logs.filter((l) => l.status === 'PENDIENTE').length;
    const ratio = logs.length > 0 ? Math.round((okCount / logs.length) * 100) : null;
    const latPromedio = latenciasOK.length
      ? Math.round(latenciasOK.reduce((a, b) => a + b, 0) / latenciasOK.length)
      : null;
    return { okCount, failed, pendiente, ratio, latPromedio, total: logs.length };
  }, [logs, latenciasOK]);

  return (
    <Card className="border-slate-200" data-testid="card-healthcheck-panel">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <ShieldCheck className="w-5 h-5 text-emerald-600" />
          Healthcheck del pipeline Inbound
        </CardTitle>
        <CardDescription>
          Verificación automática del flujo SendGrid → backend dos veces al día (9:00 y 14:00, hora Lima).
          Si la confirmación no llega en 15 min se envía alerta a los <strong>correos de copia</strong> de la empresa.
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-5">
        {/* Hero: estado y acciones */}
        <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-4 items-center p-4 rounded-lg border bg-slate-50">
          <div className="space-y-1.5">
            <div className="flex items-center gap-2">
              {ultimo ? (
                <UltimoEstadoBadge log={ultimo} />
              ) : (
                <span className="text-sm text-slate-500 italic">Sin healthchecks registrados aún.</span>
              )}
              {stats.total > 0 && (
                <span className="text-xs text-slate-500 ml-2" data-testid="hc-stats">
                  {stats.ratio}% OK · {stats.failed} FAILED · {stats.latPromedio != null ? `${stats.latPromedio}s promedio` : 'sin latencia'}
                </span>
              )}
            </div>
            {ultimo && (
              <p className="text-xs text-slate-500">
                Último envío: {fmtDate(ultimo.enviado_at)}
                {ultimo.recibido_at && ` · recibido: ${fmtDate(ultimo.recibido_at)}`}
                {ultimo.latencia_segs != null && ` · latencia: ${ultimo.latencia_segs}s`}
              </p>
            )}
          </div>
          <div className="flex flex-wrap gap-2 justify-end">
            <Button
              size="sm"
              variant="outline"
              onClick={fetchLogs}
              disabled={loading}
              data-testid="btn-hc-refresh"
            >
              <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refrescar
            </Button>
            {isAdmin && (
              <Button
                size="sm"
                onClick={verificar}
                disabled={verificando}
                data-testid="btn-hc-probar-ahora"
              >
                {verificando ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <ShieldCheck className="w-3.5 h-3.5 mr-1" />}
                Probar ahora
              </Button>
            )}
          </div>
        </div>

        {/* Sparkline */}
        <div className="space-y-1.5">
          <div className="flex items-center gap-2 text-xs font-medium text-slate-600">
            <Activity className="w-3.5 h-3.5" /> Latencia de las últimas {latenciasOK.length} verificaciones OK (segundos)
          </div>
          <div className="bg-white p-3 rounded border border-slate-100">
            <Sparkline values={latenciasOK} />
          </div>
        </div>

        {/* Tabla de logs */}
        <div>
          <div className="text-xs font-medium text-slate-600 mb-2">Historial reciente</div>
          {loading ? (
            <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
          ) : logs.length === 0 ? (
            <p className="text-sm text-slate-400 italic py-6 text-center">Sin healthchecks registrados.</p>
          ) : (
            <div className="border rounded-md overflow-hidden">
              <table className="w-full text-xs" data-testid="hc-logs-table">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr className="text-slate-600">
                    <th className="text-left px-3 py-2 font-medium">Status</th>
                    <th className="text-left px-3 py-2 font-medium">Enviado</th>
                    <th className="text-left px-3 py-2 font-medium">Recibido</th>
                    <th className="text-right px-3 py-2 font-medium">Latencia</th>
                    <th className="text-center px-3 py-2 font-medium">Alerta</th>
                    <th className="text-left px-3 py-2 font-medium">Token</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((l) => {
                    const s = STATUS_STYLES[l.status] || STATUS_STYLES.PENDIENTE;
                    const Icon = s.icon;
                    return (
                      <tr key={l.id} className="border-t border-slate-100 hover:bg-slate-50">
                        <td className="px-3 py-2">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border ${s.cls}`}>
                            <Icon className="w-3 h-3" /> {s.label}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-slate-700">{fmtDate(l.enviado_at)}</td>
                        <td className="px-3 py-2 text-slate-700">{fmtDate(l.recibido_at)}</td>
                        <td className="px-3 py-2 text-right tabular-nums">
                          {l.latencia_segs != null ? `${l.latencia_segs}s` : '—'}
                        </td>
                        <td className="px-3 py-2 text-center">
                          {l.alerta_enviada ? (
                            <Badge variant="outline" className="text-[10px] py-0 border-rose-200 text-rose-700">enviada</Badge>
                          ) : (
                            <span className="text-slate-400">—</span>
                          )}
                        </td>
                        <td className="px-3 py-2 font-mono text-[10px] text-slate-500 truncate max-w-[140px]" title={l.marker_token}>
                          {l.marker_token?.slice(0, 16)}…
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function UltimoEstadoBadge({ log }) {
  const s = STATUS_STYLES[log.status] || STATUS_STYLES.PENDIENTE;
  const Icon = s.icon;
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-medium ${s.cls}`}
      data-testid="hc-ultimo-estado"
    >
      <Icon className="w-3.5 h-3.5" /> Estado actual: {s.label}
    </span>
  );
}
